import {
  AccountType,
  CompanyOnboardingStepKey,
  getV1CompaniesCompanyIdAccounts,
  getV1CompaniesCompanyIdAddOns,
  getV1CompaniesCompanyIdDepartments,
  getV1CompaniesCompanyIdDrivers,
  getV1CompaniesCompanyIdMaterials,
  getV1CompaniesCompanyIdSites,
  getV1CompaniesCompanyIdUsers,
  getV1CompaniesIdEquipment,
} from '@treadinc/horizon-api-spec';
import { AxiosResponse } from 'axios';

import { Account } from '~hooks/useAccount/models';
import { AddOn } from '~hooks/useAddOns/models';
import { Department } from '~hooks/useDepartment/models';
import { Driver } from '~hooks/useDrivers/models';
import { Equipment } from '~hooks/useEquipment/models';
import { Material } from '~hooks/useMaterials/models';
import { Site } from '~hooks/useSites/models';
import { User } from '~hooks/useUsers/models';
import { Nullable } from '~types/Nullable';
import { parseApiPage } from '~utils/fetchAllPages';

// One page previews the records; the panel links to the Settings page for the rest.
const PAGE_LIMIT = 50;

interface CompanyOnboardingRecord {
  detail: Nullable<string>;
  id: string;
  name: string;
}

interface CompanyOnboardingRecordsPage {
  hasMore: boolean;
  records: CompanyOnboardingRecord[];
}

type CompanyOnboardingRecordsFetcher = (
  companyId: string,
  signal?: AbortSignal,
) => Promise<CompanyOnboardingRecordsPage>;

const firstPage = (companyId: string, signal?: AbortSignal) => ({
  path: { 'company-id': companyId },
  query: { 'page[limit]': PAGE_LIMIT },
  signal,
});

const recordsFetcher =
  <Proto extends { id: string }>(
    fetchFirstPage: (
      companyId: string,
      signal?: AbortSignal,
    ) => Promise<AxiosResponse<{ data: Proto[] }>>,
    describe: (proto: Proto) => Omit<CompanyOnboardingRecord, 'id'>,
  ) =>
  async (
    companyId: string,
    signal?: AbortSignal,
  ): Promise<CompanyOnboardingRecordsPage> => {
    const { data, pagination } = parseApiPage(
      await fetchFirstPage(companyId, signal),
      (proto) => ({
        id: proto.id,
        ...describe(proto),
      }),
    );

    return { hasMore: Boolean(pagination.after), records: data };
  };

const accountRecordsFetcher = (accountType: AccountType) =>
  recordsFetcher(
    (companyId, signal) =>
      getV1CompaniesCompanyIdAccounts({
        path: { 'company-id': companyId },
        query: {
          'filter[account_types]': [accountType],
          'page[limit]': PAGE_LIMIT,
        },
        signal,
      }),
    (proto) => {
      const account = Account.parse(proto);

      return { detail: account.externalId || null, name: account.name };
    },
  );

export const companyOnboardingRecordFetchers = {
  [CompanyOnboardingStepKey.CUSTOMERS]: accountRecordsFetcher(AccountType.CUSTOMER),
  [CompanyOnboardingStepKey.FREIGHT_RATES]: recordsFetcher(
    (companyId, signal) => getV1CompaniesCompanyIdAddOns(firstPage(companyId, signal)),
    (proto) => {
      const addOn = AddOn.parse(proto);

      return { detail: addOn.externalId, name: addOn.name };
    },
  ),
  [CompanyOnboardingStepKey.DEPARTMENTS_CLASSES]: recordsFetcher(
    (companyId, signal) =>
      getV1CompaniesCompanyIdDepartments(firstPage(companyId, signal)),
    (proto) => ({ detail: null, name: Department.parse(proto).name }),
  ),
  [CompanyOnboardingStepKey.DRIVERS]: recordsFetcher(
    (companyId, signal) => getV1CompaniesCompanyIdDrivers(firstPage(companyId, signal)),
    (proto) => {
      const driver = Driver.parse(proto);

      return { detail: driver.phone, name: driver.fullName };
    },
  ),
  [CompanyOnboardingStepKey.EQUIPMENT]: recordsFetcher(
    (companyId, signal) => getV1CompaniesIdEquipment(firstPage(companyId, signal)),
    (proto) => {
      const equipment = Equipment.parse(proto);

      return { detail: equipment.typeName, name: equipment.name };
    },
  ),
  [CompanyOnboardingStepKey.MATERIALS]: recordsFetcher(
    (companyId, signal) => getV1CompaniesCompanyIdMaterials(firstPage(companyId, signal)),
    (proto) => {
      const material = Material.parse(proto);

      return { detail: material.typeName, name: material.name };
    },
  ),
  [CompanyOnboardingStepKey.SITES]: recordsFetcher(
    (companyId, signal) => getV1CompaniesCompanyIdSites(firstPage(companyId, signal)),
    (proto) => {
      const site = Site.parse(proto);

      return { detail: site.fullAddress, name: site.name };
    },
  ),
  [CompanyOnboardingStepKey.USERS]: recordsFetcher(
    (companyId, signal) => getV1CompaniesCompanyIdUsers(firstPage(companyId, signal)),
    (proto) => {
      const user = User.parse(proto);

      return {
        detail: user.userRoles?.map((role) => role.name).join(', ') || null,
        name: user.fullName,
      };
    },
  ),
  [CompanyOnboardingStepKey.VENDORS]: accountRecordsFetcher(AccountType.VENDOR),
} satisfies Partial<Record<CompanyOnboardingStepKey, CompanyOnboardingRecordsFetcher>>;

export type CompanyOnboardingRecordsStepKey =
  keyof typeof companyOnboardingRecordFetchers;

export const hasCompanyOnboardingRecords = (
  stepKey: CompanyOnboardingStepKey,
): stepKey is CompanyOnboardingRecordsStepKey =>
  Object.hasOwn(companyOnboardingRecordFetchers, stepKey);
